import React from 'react';
import {
  ChevronUp,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
  Check,
  ChevronsUpDown,
  Search,
} from 'lucide-react';
import { BillDetailRow, BillSortKey, DueStatus } from '../api';
import { Skeleton } from 'components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Command, CommandGroup, CommandItem } from 'components/ui/command';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { Download } from 'lucide-react';

interface Props {
  data: BillDetailRow[];
  loading: boolean;
  customerName: string;
  collectionResponsibilityName?: string | null;
  page: number;
  pageSize: number;
  totalItems: number;
  sortBy: BillSortKey;
  sortOrder: 'asc' | 'desc';
  onSortChange: (col: BillSortKey) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onBack: () => void;
  conversionType: 'INR' | 'Lakhs' | 'Crores';
  onConversionChange: (type: 'INR' | 'Lakhs' | 'Crores') => void;
  search: string;
  dueStatus: DueStatus | undefined;
  onSearchChange: (value: string) => void;
  onDueStatusChange: (value: DueStatus | undefined) => void;
  onDownloadExcel: () => void;
  downloadingExcel: boolean;
}

const columns: Array<{
  key: BillSortKey;
  label: string;
  isNumeric: boolean;
}> = [
    { key: 'reference_no', label: 'Reference No', isNumeric: false },
    { key: 'branch_name', label: 'Branch Name', isNumeric: false },
    { key: 'bill_date', label: 'Bill Date', isNumeric: false },
    { key: 'due_date', label: 'Due Date', isNumeric: false },
    { key: 'overdue_days', label: 'Overdue Days', isNumeric: true },
    { key: 'transaction_amt', label: 'Transaction Amount', isNumeric: true },
    { key: 'outstanding', label: 'Outstanding', isNumeric: true },
  ];

const DUE_STATUS_OPTIONS: Array<{
  label: string;
  value: DueStatus;
}> = [
    { label: 'Not Due', value: 'not_due' },
    { label: 'Overdue', value: 'overdue' },
  ];

const ShortPaymentsBillDetailsTable: React.FC<Props> = ({
  data,
  loading,
  customerName,
  collectionResponsibilityName,
  page,
  pageSize,
  totalItems,
  sortBy,
  sortOrder,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  onBack,
  conversionType,
  onConversionChange,
  search,
  dueStatus,
  onSearchChange,
  onDueStatusChange,
  onDownloadExcel,
  downloadingExcel,
}) => {
  const [dueStatusOpen, setDueStatusOpen] = React.useState<boolean>(false);
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const renderLoadingRows = () =>
    Array.from({ length: 10 }).map((_, i) => (
      <tr key={i}>
        {columns.map((_, j) => (
          <td key={j} className="border border-gray-200 px-2 py-1.5">
            <Skeleton className="h-4 w-full" />
          </td>
        ))}
      </tr>
    ));

  const formatNumber = (value: number): string => {
    let displayValue = value;

    switch (conversionType) {
      case 'Lakhs':
        displayValue = value / 100000;
        break;
      case 'Crores':
        displayValue = value / 10000000;
        break;
      case 'INR':
      default:
        displayValue = value;
    }

    return displayValue.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (date?: string | null): string => {
    if (!date) return '-';
    const d = new Date(date);
    return isNaN(d.getTime())
      ? '-'
      : d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="shrink-0 space-y-4 pb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="px-3 py-1 text-sm rounded bg-gray-200 hover:bg-gray-300">
              ← Back
            </button>
            <h2 className="text-lg font-semibold">
              Bills for {customerName}
              {collectionResponsibilityName && <span> — {collectionResponsibilityName}</span>}
            </h2>
          </div>

          <div className="flex gap-2">
            {(['INR', 'Lakhs', 'Crores'] as const).map((t) => (
              <button
                key={t}
                onClick={() => onConversionChange(t)}
                className={cn(
                  'px-3 py-1 text-sm rounded-md',
                  conversionType === t ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                )}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-end justify-between gap-4">
          <div className="flex gap-4 items-end">
            <div>
              <label className="block text-sm font-medium mb-1">Search Bill No</label>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder="Search by bill number..."
                  className="w-full h-10 rounded-md border pl-9 pr-3 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Due Status</label>
              <Popover open={dueStatusOpen} onOpenChange={setDueStatusOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-48 justify-between">
                    {dueStatus ? DUE_STATUS_OPTIONS.find((o) => o.value === dueStatus)?.label : '-- select --'}
                    <ChevronsUpDown className="h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>

                <PopoverContent className="w-[200px] p-0">
                  <Command>
                    <CommandGroup>
                      {DUE_STATUS_OPTIONS.map((opt) => (
                        <CommandItem
                          key={opt.value}
                          onSelect={() => {
                            onDueStatusChange(dueStatus === opt.value ? undefined : opt.value);
                            setDueStatusOpen(false);
                          }}
                        >
                          <Check className={cn('mr-2 h-4 w-4', dueStatus === opt.value ? 'opacity-100' : 'opacity-0')} />
                          {opt.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <button
            className="px-3 py-1 text-sm rounded-md border border-green-600 text-green-700 hover:bg-green-50 transition-colors flex items-center gap-1"
            onClick={onDownloadExcel}
            disabled={downloadingExcel}
          >
            <Download size={16} />
            {downloadingExcel ? 'Downloading...' : 'Excel'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto min-h-0 border">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-gray-100">
            <tr className="bg-gray-100">
              {columns.map((col) => {
                const isActive = sortBy === col.key;
                return (
                  <th
                    key={col.key}
                    onClick={() => onSortChange(col.key as BillSortKey)}
                    className={`border border-gray-300 px-2 py-2 font-bold cursor-pointer select-none ${col.isNumeric ? 'text-center' : 'text-left'
                      }`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      {col.label}
                      {isActive ? (
                        sortOrder === 'asc' ? (
                          <ChevronUp size={14} />
                        ) : (
                          <ChevronDown size={14} />
                        )
                      ) : (
                        <ChevronUp size={14} className="text-gray-300" />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              renderLoadingRows()
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-20 text-gray-500">
                  No bills found.
                </td>
              </tr>
            ) : (
              data.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="border px-2 py-1.5">{row.reference_no}</td>
                  <td className="border px-2 py-1.5">{row.branch_name}</td>
                  <td className="border px-2 py-1.5">{formatDate(row.bill_date)}</td>
                  <td className="border px-2 py-1.5">{formatDate(row.due_date)}</td>
                  <td className="border px-2 py-1.5 text-center">{row.overdue_days ?? '-'}</td>
                  <td className="border px-2 py-1.5 text-right">{formatNumber(row.transaction_amt)}</td>
                  <td className="border px-2 py-1.5 text-right">{formatNumber(row.outstanding)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between shrink-0 pt-4">
        <span className="text-sm text-gray-600">
          Showing <strong>{totalItems === 0 ? 0 : (page - 1) * pageSize + 1}</strong> to{' '}
          <strong>{Math.min(page * pageSize, totalItems)}</strong> of <strong>{totalItems}</strong> results
        </span>

        <div className="flex items-center gap-10 text-sm">
          <div className="flex items-center gap-1">
            <span className="text-gray-600 mr-1">Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1"
            >
              {[10, 20, 50, 100].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <span className="text-gray-600">
            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
          </span>

          <div className="flex items-center gap-1">
            <button
              disabled={page === 1}
              onClick={() => onPageChange(1)}
              className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
            >
              <ChevronsLeft size={16} />
            </button>

            <button
              disabled={page === 1}
              onClick={() => onPageChange(page - 1)}
              className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
            >
              <ChevronLeft size={16} />
            </button>

            <button
              disabled={page === totalPages}
              onClick={() => onPageChange(page + 1)}
              className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
            >
              <ChevronRight size={16} />
            </button>

            <button
              disabled={page === totalPages}
              onClick={() => onPageChange(totalPages)}
              className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShortPaymentsBillDetailsTable;

